import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import * as labActions from '../../../redux/Laboratory/LaboratoryAction';

//ui components
import CircularProgress from '@material-ui/core/CircularProgress';
import Rows from './Rows';
import AddFab from '../AddFab';

//utils
import makeStyles from '@material-ui/core/styles/makeStyles';
import MessagesContext from '../../../contexts/messagesContext';

//assets
import LabResultImage from '../../../assets/images/lab-results.svg';
import { Typography } from '@material-ui/core';
import useCheckAccess from '../../../hooks/useCheckAccess';

const useStyle = makeStyles((theme) => ({
    container: {
        display: 'flex',
        flexDirection: 'column',
        fontSize: 15,
        position: 'relative',
        minHeight: 400,
        marginTop: '12px',
        // paddingLeft: props.isMobile && '12px',
        [theme.breakpoints.down(1025)]: {
            marginTop: '30px'
        }
    },
    loading: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: '100%',
        position: 'absolute',
        zIndex: 1,
        background: 'white'
    },
    emptyComponent: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        '& img': {
            maxWidth: '50%',
            width: 200
        }
    },
    emptyMessage: {
        marginTop: 20,
        fontFamily: 'inherit',
        fontSize: '0.9rem',
        fontWeight: 'normal',
        fontStretch: 'normal',
        fontStyle: 'normal',
        lineHeight: 1.75,
        letterSpacing: 'normal',
        textAlign: 'center',
        color: '#bbb'
    }
}));

export default function LabResult() {
    const { messages } = useContext(MessagesContext);
    const classes = useStyle();
    const laboratoryTestGroups = useSelector((store) => store.laboratory?.laboratoryTestGroups);
    const [labTests, setLabTests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isRendering, setIsRendering] = useState(labTests?.length ? true : false);
    const history = useHistory();
    const params = useParams();
    const location = useLocation();
    const dispatch = useDispatch();
    const { notEditClinical } = useCheckAccess();
    const commonPath = `/${params.clinicId}/patient/${params.id}/laboratory/result`;

    useEffect(() => {
        setLabTests(laboratoryTestGroups);
        setLoading(false);
    }, [laboratoryTestGroups]);

    const handleAddResult = () => {
        history.push(`${commonPath}/form/new`, { location });
    };

    const handleSaveValue = useCallback(
        (groupId, resultId, valueId, newValue) => {
            const group = labTests.filter((group) => group.id === groupId)[0];
            if (!group) return;
            const result = group.results.filter((result) => result.id === resultId)[0];
            if (!result) return;
            const values = result.values
                .map((v) =>
                    v.id === valueId
                        ? { factor_id: v.factor_id, value: newValue }
                        : { factor_id: v.factor_id, value: v.value }
                )
                .filter((v) => v.value);

            const data = {
                issued_datetime: result.issued_datetime,
                patient_id: params.id,
                values: values
            };
            dispatch(
                labActions.editLaboratoryResult({
                    data,
                    result_id: resultId,
                    group_id: groupId,
                    patient_id: params.id
                })
            );
        },
        //eslint-disable-next-line
        [labTests, params.id]
    );
    const _renderItems = useMemo(
        () =>
            labTests?.map((v, i, arr) => {
                if (i === arr.length - 1) {
                    setIsRendering(false);
                }
                return (
                    <Rows
                        data={v}
                        key={`result-groupe-${v.id}`}
                        resultScreenPath={commonPath}
                        handleSaveValue={handleSaveValue}
                    />
                );
            }),
        [labTests, commonPath, handleSaveValue]
    );

    return (
        <>
            <div className={classes.container}>
                {(loading || isRendering) && (
                    <div className={classes.loading}>
                        <CircularProgress />
                    </div>
                )}
                <div className={classes.container}>
                    {_renderItems?.length ? (
                        _renderItems
                    ) : (
                        <div className={classes.emptyComponent}>
                            <img src={LabResultImage} alt="there is no result here" />
                            <Typography className={classes.emptyMessage} variant="subtitle1">
                                {messages.noLabDataFound}
                            </Typography>
                        </div>
                    )}
                </div>
            </div>
            <AddFab
                onClick={handleAddResult}
                bottom={20}
                right={30}
                // left={lang === 'en' ? 30 : 'unset'}
                position="absolute"
                disabled={notEditClinical}
            />
        </>
    );
}
